import React, { useState, useEffect, useCallback } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import { PortalDealsTable, PortalBrowseDeals, PortalDealsStats, PortalDealDetail, PortalGpInProgress, PORTAL_STAGE_FILTERS, PORTAL_BROWSE_FILTERS } from '../components/Layout/PortalDealsViews'
import PortalPostDealForm from '../components/Layout/PortalPostDealForm'
import UpgradeModal from '../components/UpgradeModal'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import { RsIcon, ModalClose } from '../components/icons'
import axios from 'axios'
import toast from 'react-hot-toast'
import { mapDealFromApi, fetchDealWithProgress, formatMoney, parseMoneyAmount } from '../utils/dealMapper'

const BROWSE_ROLES = ['LP', 'Startup', 'ServiceProvider', 'Admin', 'GP']

function EscrowPanel({ dealId, role, isParticipant }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState({ amount: '', referenceNo: '', notes: '' })

  const reload = () => {
    setLoading(true)
    axios
      .get(`/api/deals/${dealId}/escrow`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load escrow details'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    reload()
  }, [dealId])

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await axios.post(`/api/deals/${dealId}/escrow/submit`, form)
      toast.success('Wire record submitted — awaiting Admin confirmation')
      setForm({ amount: '', referenceNo: '', notes: '' })
      reload()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Submission failed')
    }
  }

  const handleConfirm = async (eventId) => {
    try {
      await axios.post(`/api/deals/escrow-events/${eventId}/confirm`)
      toast.success('Receipt confirmed')
      reload()
    } catch (err) {
      toast.error(err.response?.data?.message || 'Confirmation failed')
    }
  }

  if (loading) return <p className="text-xs text-gray-400 px-5 py-3">Loading escrow data…</p>
  if (!data) return null

  const canSubmit = ['LP', 'Admin'].includes(role) && (role === 'Admin' || isParticipant)

  return (
    <div className="px-5 py-4 border-t border-gray-100 bg-amber-50/50">
      <h4 className="text-sm font-semibold text-navy mb-1">$ Escrow Registration</h4>
      <p className="text-xs text-gray-500 mb-3">{data.disclaimer}</p>
      <div className="grid grid-cols-3 gap-3 mb-4 text-center">
        <div className="bg-white rounded-lg p-2 border">
          <p className="text-xs text-gray-500">Target</p>
          <p className="font-bold text-navy">{data.targetAmount?.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg p-2 border">
          <p className="text-xs text-gray-500">Confirmed</p>
          <p className="font-bold text-green-700">{data.confirmedEscrowTotal?.toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-lg p-2 border">
          <p className="text-xs text-gray-500">Pending</p>
          <p className="font-bold text-amber-700">{data.pendingEscrowTotal?.toLocaleString()}</p>
        </div>
      </div>

      {canSubmit && (
        <form onSubmit={handleSubmit} className="grid grid-cols-3 gap-2 mb-4">
          <input
            className="input-field text-sm"
            placeholder="Amount (CNY)"
            type="number"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
          <input
            className="input-field text-sm"
            placeholder="Wire reference no."
            value={form.referenceNo}
            onChange={(e) => setForm((f) => ({ ...f, referenceNo: e.target.value }))}
            required
          />
          <button type="submit" className="btn-primary text-sm py-2">
            Register wire
          </button>
        </form>
      )}

      <div className="space-y-2">
        {data.events?.length ? (
          data.events.map((ev) => (
            <div key={ev.id} className="flex justify-between items-center p-2 bg-white rounded-lg border text-sm">
              <div>
                <span className="font-medium">{ev.submitterName}</span>
                <span className="text-gray-500 ml-2">
                  ¥{ev.amount?.toLocaleString()} · {ev.referenceNo}
                </span>
                <span className={`ml-2 badge ${ev.status === 'confirmed' ? 'badge-green' : 'badge-yellow'}`}>
                  {ev.status}
                </span>
              </div>
              {role === 'Admin' && ev.status === 'pending' && (
                <button
                  type="button"
                  onClick={() => handleConfirm(ev.id)}
                  className="px-2 py-1 text-xs bg-green-600 text-white rounded"
                >
                  Confirm receipt
                </button>
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-400">No wire records yet</p>
        )}
      </div>
    </div>
  )
}

function MatchedLpsPanel({ dealId }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    setLoading(true)
    axios
      .get(`/api/deals/${dealId}/matched-lps`)
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load LP matches'))
      .finally(() => setLoading(false))
  }, [dealId])

  const handleInvite = async (lpUserId) => {
    try {
      const res = await axios.post(`/api/deals/${dealId}/invite-lp`, { lpUserId })
      toast.success(res.data?.message || 'Invitation sent')
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invitation failed')
    }
  }

  if (loading) return <p className="text-xs text-gray-400 px-5 py-3">Matching LPs…</p>
  if (!data?.matches?.length) {
    return (
      <div className="px-5 py-3 border-t border-gray-100 bg-blue-50/50">
        <p className="text-sm text-gray-600">No rule-based matches yet. Check deal industry tags or wait for more LP registrations.</p>
      </div>
    )
  }

  return (
    <div className="px-5 py-4 border-t border-gray-100 bg-blue-50/40">
      <h4 className="text-sm font-semibold text-navy mb-2">🎯 LP match suggestions (rules engine)</h4>
      <p className="text-xs text-gray-500 mb-3">
        {data.totalCandidates} candidates — showing top {data.matches.length} (industry / paid tier weighted)
      </p>
      <div className="space-y-2">
        {data.aiNote && (
          <p className="text-xs text-blue-600 mb-2">{data.aiNote}</p>
        )}
        {data.matches.map((m) => (
          <div key={m.userId} className="flex items-center justify-between p-2 bg-white rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-navy">{m.systemName}</p>
              <p className="text-xs text-gray-500">
                {m.company} · score {m.matchScore}
                {m.matchReasons?.length ? ` · ${m.matchReasons.join(', ')}` : ''}
              </p>
              {m.aiReason && <p className="text-xs text-indigo-600 mt-0.5">AI: {m.aiReason}</p>}
            </div>
            <button
              type="button"
              onClick={() => handleInvite(m.userId)}
              className="px-3 py-1 text-xs font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600"
            >
              Invite
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}

const DEAL_PHASES = [
  { key: 'posted', label: 'Deal Posted', icon: 'briefcase', role: 'GP', description: 'GP publishes the deal' },
  { key: 'admin_review', label: 'Admin Review', icon: 'check', role: 'Admin', description: 'Platform compliance review' },
  { key: 'matching', label: 'LP Matching', icon: 'users', role: 'System', description: 'AI matches potential LPs' },
  { key: 'nda', label: 'NDA Signed', icon: 'lock', role: 'Both', description: 'NDA signed (offline registration + Contracts tracking)' },
  { key: 'dd', label: 'Due Diligence', icon: 'fileText', role: 'Both', description: 'Full due diligence' },
  { key: 'term_sheet', label: 'Term Sheet', icon: 'fileText', role: 'Both', description: 'Investment terms negotiation' },
  { key: 'escrow', label: 'Escrow Funding', icon: 'dollarSign', role: 'LP', description: 'Escrow funding registration' },
  { key: 'closed', label: 'Deal Closed', icon: 'check', role: 'System', description: 'Deal completed' },
]

const PHASE_STATUS = {
  completed: { color: 'bg-green-500', line: 'bg-green-500', icon: 'text-green-600' },
  active: { color: 'bg-primary-500 animate-pulse', line: 'bg-primary-500', icon: 'text-primary-600' },
  pending: { color: 'bg-gray-300', line: 'bg-gray-200', icon: 'text-gray-400' },
}

export default function Deals() {
  const { user, role, isCompanyPaid } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const canBrowse = BROWSE_ROLES.includes(role)
  const isPortal = PORTAL_ROLES.includes(role)
  const isPostDealPage = isPortal && role === 'GP' && searchParams.get('action') === 'create'
  const listMode = canBrowse && searchParams.get('tab') === 'browse' ? 'browse' : 'my'
  const [deals, setDeals] = useState([])
  const [loadingDeals, setLoadingDeals] = useState(true)
  const [pageStats, setPageStats] = useState(null)
  const [selectedDeal, setSelectedDeal] = useState(null)
  const [showCreate, setShowCreate] = useState(false)
  const [creatingDeal, setCreatingDeal] = useState(false)
  const [showPaywall, setShowPaywall] = useState(false)
  const [activeTab, setActiveTab] = useState('active')
  const [portalStageFilter, setPortalStageFilter] = useState('all')
  const [portalBrowseFilter, setPortalBrowseFilter] = useState('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showTaskModal, setShowTaskModal] = useState(false)
  const [taskTitle, setTaskTitle] = useState('')
  const [addingTask, setAddingTask] = useState(false)
  const [newDeal, setNewDeal] = useState({
    title: '',
    description: '',
    targetAmount: '',
    industry: '',
    location: '',
  })

  const loadDeals = useCallback(async (query = '') => {
    setLoadingDeals(true)
    try {
      if (listMode === 'browse') {
        const res = await axios.get('/api/deals/browse', {
          params: query ? { search: query } : {},
        })
        setDeals((res.data.items || []).map((row) => mapDealFromApi(row, { currentPhase: row.phase, progress: row.progress })))
      } else {
        const res = await axios.get('/api/deals/my-deals')
        setDeals(res.data.map((row) => mapDealFromApi(row)))
      }
    } catch {
      toast.error('Failed to load deals')
    } finally {
      setLoadingDeals(false)
    }
  }, [listMode])

  useEffect(() => {
    loadDeals()
  }, [loadDeals])

  useEffect(() => {
    axios.get('/api/deals/page-stats').then((r) => setPageStats(r.data)).catch(() => {})
  }, [listMode])

  useEffect(() => {
    if (searchParams.get('action') === 'create' && role === 'GP' && !isPortal) {
      setShowCreate(true)
    }
  }, [searchParams, role, isPortal])

  useEffect(() => {
    if (searchParams.get('action') === 'create' && role === 'GP' && isPortal && !isCompanyPaid) {
      setShowPaywall(true)
      setSearchParams({})
    }
  }, [searchParams.get('action'), role, isPortal, isCompanyPaid, setSearchParams])

  useEffect(() => {
    const selectedId = searchParams.get('selected')
    if (!selectedId) return
    if (canBrowse && searchParams.get('tab') !== 'browse') {
      setSearchParams({ tab: 'browse', selected: selectedId }, { replace: true })
      return
    }
    if (selectedDeal?.id === selectedId) return
    const existing = deals.find((d) => d.id === selectedId)
    if (existing) setSelectedDeal(existing)
    fetchDealWithProgress(axios, selectedId)
      .then((full) => {
        setSelectedDeal(full)
        setDeals((prev) => (prev.some((d) => d.id === full.id) ? prev.map((d) => (d.id === full.id ? full : d)) : [full, ...prev]))
      })
      .catch(() => toast.error('Could not open deal details'))
  }, [searchParams.get('selected'), searchParams.get('tab'), canBrowse])

  const closeDealPreview = () => {
    setSelectedDeal(null)
    if (listMode === 'browse') {
      setSearchParams({ tab: 'browse' }, { replace: true })
    }
  }

  const setListMode = (mode) => {
    if (mode === 'browse') setSearchParams({ tab: 'browse' })
    else setSearchParams({})
    setSelectedDeal(null)
  }

  const handleSelectDeal = async (deal) => {
    setSelectedDeal(deal)
    if (listMode === 'browse') {
      setSearchParams({ tab: 'browse', selected: deal.id }, { replace: true })
    }
    try {
      const full = await fetchDealWithProgress(axios, deal.id)
      setSelectedDeal(full)
      setDeals((prev) => prev.map((d) => (d.id === full.id ? full : d)))
    } catch {
      toast.error('Failed to load deal progress')
    }
  }

  const searchFilteredDeals = deals.filter((d) => {
    const q = searchQuery.toLowerCase()
    return (
      (d.title || '').toLowerCase().includes(q) ||
      (d.gp || '').toLowerCase().includes(q)
    )
  })

  const filteredDeals = searchFilteredDeals.filter((d) => {
    const matchesTab =
      activeTab === 'all' ||
      (activeTab === 'active' && d.currentPhase !== 'closed') ||
      (activeTab === 'closed' && d.currentPhase === 'closed')
    let matchesPortalFilter = true
    if (isPortal && listMode === 'my' && portalStageFilter !== 'all') {
      const spec = PORTAL_STAGE_FILTERS.find((f) => f.id === portalStageFilter)
      matchesPortalFilter = spec?.phases?.includes(d.currentPhase) ?? true
    }
    if (isPortal && listMode === 'browse' && portalBrowseFilter !== 'all') {
      const spec = PORTAL_BROWSE_FILTERS.find((f) => f.id === portalBrowseFilter)
      matchesPortalFilter = spec?.match ? spec.match(d) : true
    }
    return matchesTab && matchesPortalFilter
  })

  const closePostDeal = () => {
    setSearchParams({})
    setShowCreate(false)
  }

  const handleCreateDeal = async (input) => {
    const isFormEvent = input?.preventDefault
    if (isFormEvent) input.preventDefault()
    if (!isCompanyPaid) {
      setShowPaywall(true)
      return
    }
    const body = isFormEvent ? newDeal : input
    if (role !== 'GP') {
      toast.error('Only GP can post deals')
      return
    }
    const parsedAmount = parseMoneyAmount(body.targetAmount)
    if (body.targetAmount && parsedAmount == null) {
      toast.error('Target amount must be a valid number (e.g. $500M or 1000000)')
      return
    }
    const payload = { ...body, targetAmount: parsedAmount }
    setCreatingDeal(true)
    try {
      const res = await axios.post('/api/deals', payload)
      toast.success('Deal posted successfully!')
      setDeals((prev) => [mapDealFromApi(res.data), ...prev])
      setNewDeal({ title: '', description: '', targetAmount: '', industry: '', location: '' })
      if (isPostDealPage) {
        setSearchParams({})
      } else {
        setShowCreate(false)
      }
    } catch (err) {
      if (err.response?.status === 402 || err.response?.data?.code === 'COMPANY_PAYWALL') {
        setShowPaywall(true)
        return
      }
      toast.error('Failed to post deal')
    } finally {
      setCreatingDeal(false)
    }
  }

  const handleCompleteAction = async (dealId, actionId) => {
    try {
      await axios.post(`/api/deals/${dealId}/actions/${actionId}/complete`, {
        completedBy: user?.id,
        notes: ''
      })
      toast.success('Action completed!')
      const full = await fetchDealWithProgress(axios, dealId)
      setSelectedDeal(full)
      setDeals((prev) => prev.map((d) => (d.id === dealId ? full : d)))
    } catch (err) {
      toast.error('Failed to complete action')
    }
  }

  const handleAdvancePhase = async (dealId, from, to) => {
    try {
      await axios.post(`/api/deals/${dealId}/advance-phase`, {
        from,
        to,
        approvedBy: user?.id
      })
      toast.success(`Advanced to ${to} phase!`)
      const full = await fetchDealWithProgress(axios, dealId)
      setSelectedDeal(full)
      setDeals((prev) => prev.map((d) => (d.id === dealId ? full : d)))
    } catch (err) {
      toast.error('Failed to advance phase')
    }
  }

  const handleJoinDeal = async (dealId) => {
    try {
      const res = await axios.post(`/api/deals/${dealId}/join`)
      toast.success(res.data?.message || 'Joined portfolio')
      await loadDeals()
      if (selectedDeal?.id === dealId) {
        const full = await fetchDealWithProgress(axios, dealId)
        setSelectedDeal(full)
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to join deal')
    }
  }

  const handleOpenChat = async (deal) => {
    if (listMode === 'browse') {
      try {
        if (role === 'Admin') {
          closeDealPreview()
          navigate(`/admin/chat?deal=${deal.id}&createGroup=1`)
          toast.success('Open matchmaking — create a group for this deal')
          return
        }

        const matchRes = await axios.post(`/api/deals/${deal.id}/match-request`)
        const adminId = matchRes.data?.adminId
        if (!adminId) {
          toast.error('Platform admin is not available')
          return
        }
        closeDealPreview()
        navigate(`/chat?contact=${adminId}&deal=${deal.id}&intent=matchmaking`)
        toast.success('Platform Admin contacted. An admin will facilitate matchmaking and create a group.')
        return
      } catch (err) {
        const data = err.response?.data || {}
        if (data?.code === 'PAYWALL' || err.response?.status === 402) {
          toast.error('Upgrade your membership to contact an admin for deal matchmaking.')
          return
        }
        toast.error(data?.message || 'Could not contact Platform Admin')
        return
      }
    }

    try {
      let targetId = deal.gpId
      if (!targetId) {
        const dealRes = await axios.get(`/api/deals/${deal.id}`)
        targetId = dealRes.data?.gp_id
      }
      if (!targetId) {
        toast.error('Could not find GP for this deal')
        return
      }
      if (String(targetId) === String(user?.id)) {
        navigate('/chat')
        toast('Select an LP or other participant from your contacts')
        return
      }
      navigate(`/chat?contact=${targetId}`)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not open chat')
    }
  }

  const handleSignNDA = async (dealId) => {
    try {
      const dealRow = selectedDeal?.id === dealId ? selectedDeal : deals.find((d) => d.id === dealId)
      let gpId = dealRow?.gpId
      if (!gpId) {
        const dealRes = await axios.get(`/api/deals/${dealId}`)
        gpId = dealRes.data?.gp_id
      }
      if (!gpId) {
        toast.error('GP not found for this deal')
        return
      }
      const res = await axios.post('/api/contracts/nda', {
        dealId,
        parties: [user?.id, gpId].filter(Boolean),
      })
      toast.success('NDA signing link sent!')
      window.open(res.data.signUrl, '_blank')
    } catch (err) {
      toast.error('Failed to initiate NDA signing')
    }
  }

  const handleExportDeals = () => {
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`
    const header = ['Deal Name', 'Amount', 'Stage', 'Progress', 'GP'].map(escape).join(',')
    const rows = searchFilteredDeals.map((d) => [
      d.title,
      d.target || formatMoney(d.targetAmount),
      d.currentPhase,
      `${d.progress || 0}%`,
      d.gp,
    ].map(escape).join(','))
    const csv = [header, ...rows].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'deals-report.csv'
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Report exported')
  }

  const portalTable = isPortal && role === 'GP' && listMode === 'my' && !isPostDealPage
  const gpInProgress = portalTable
  const portalBrowse = isPortal && listMode === 'browse' && (role === 'LP' || role === 'GP')

  useEffect(() => {
    if (!gpInProgress || loadingDeals || selectedDeal) return
    const active = searchFilteredDeals.filter((d) => d.currentPhase !== 'closed')
    if (active.length) {
      handleSelectDeal(active[0])
    }
  }, [gpInProgress, loadingDeals, selectedDeal?.id, searchFilteredDeals.length])

  const handleAddTask = () => {
    if (!selectedDeal?.id) {
      toast.error('Select a deal first')
      return
    }
    setTaskTitle('')
    setShowTaskModal(true)
  }

  const submitNewTask = async (e) => {
    e.preventDefault()
    if (!selectedDeal?.id || !taskTitle.trim()) {
      toast.error('Enter a task title')
      return
    }
    setAddingTask(true)
    try {
      await axios.post(`/api/deals/${selectedDeal.id}/actions`, {
        title: taskTitle.trim(),
        assignedRole: role || 'GP',
      })
      toast.success('Task added')
      setShowTaskModal(false)
      setTaskTitle('')
      const full = await fetchDealWithProgress(axios, selectedDeal.id)
      setSelectedDeal(full)
      setDeals((prev) => prev.map((d) => (d.id === full.id ? full : d)))
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to add task')
    } finally {
      setAddingTask(false)
    }
  }

  const pageTitle = isPostDealPage
    ? 'Post a Deal'
    : listMode === 'browse'
      ? 'Find a Deal'
      : role === 'GP'
        ? 'In Progress'
        : 'My Portfolio'

  if (isPostDealPage) {
    if (!isCompanyPaid) {
      return (
        <PageShell>
          <UpgradeModal
            open
            onClose={() => navigate('/deals')}
            onUpgrade={() => navigate('/payment')}
            message="Posting deals requires an active company subscription. Personal paid accounts cannot publish deals — please upgrade your company plan."
          />
        </PageShell>
      )
    }
    return (
      <PageShell>
        <div className="portal-page-full">
          <PortalHeader title="Post a Deal" showSearch={false} />
          <PortalPostDealForm
            onSubmit={handleCreateDeal}
            onCancel={closePostDeal}
            loading={creatingDeal}
          />
        </div>
      </PageShell>
    )
  }

  return (
    <PageShell>
        <UpgradeModal
          open={showPaywall}
          onClose={() => setShowPaywall(false)}
          onUpgrade={() => {
            setShowPaywall(false)
            navigate('/payment')
          }}
          message="Posting deals requires an active company subscription. Personal paid accounts cannot publish deals."
        />
        {gpInProgress ? (
          <PortalGpInProgress
            allDeals={searchFilteredDeals}
            selectedDeal={selectedDeal}
            onSelectDeal={handleSelectDeal}
            pageStats={pageStats}
            loading={loadingDeals}
            onExport={handleExportDeals}
            onAddTask={handleAddTask}
            onCompleteAction={handleCompleteAction}
          />
        ) : (
        <>
        {/* Header */}
        {isPortal ? (
          <>
            <PortalHeader
              title={pageTitle}
              searchValue={searchQuery}
              onSearchChange={setSearchQuery}
              onSearchSubmit={loadDeals}
            >
              {role === 'GP' && listMode !== 'browse' && (
                <button
                  type="button"
                  className="portal-btn-primary"
                  onClick={() => setSearchParams({ action: 'create' })}
                >
                  + Post Deal
                </button>
              )}
            </PortalHeader>
            <PortalDealsStats
              pageStats={pageStats}
              listMode={listMode}
              dealCount={deals.length}
              loading={loadingDeals}
              role={role}
            />
            <div className="portal-page-subtitle">
              {listMode === 'browse'
                ? 'Discover fund raises, direct investments, and co-investment opportunities.'
                : role === 'GP'
                  ? 'All ongoing deals — overview at a glance.'
                  : 'Track deals in your portfolio and pending actions.'}
            </div>
          </>
        ) : (
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-navy">Deals</h1>
            <p className="text-gray-500 text-sm mt-1">
              {listMode === 'browse' ? 'Browse active deals on the platform' : 'Manage deal lifecycle from posting to closing'}
            </p>
          </div>
          {role === 'GP' && (
            <button
              onClick={() => setShowCreate(true)}
              className="btn-primary flex items-center gap-2"
            >
              <RsIcon name="plus" size={16} className="w-4 h-4" /> Post Deal
            </button>
          )}
        </div>
        )}

        {/* Stats */}
        {!isPortal && (
        <div className="grid grid-cols-4 gap-5 mb-8">
          {[
            {
              label: listMode === 'browse' ? 'Marketplace Deals' : 'Active Deals',
              value: String(pageStats?.activeDeals ?? (loadingDeals ? '…' : deals.length)),
              icon: 'briefcase',
              color: 'blue',
            },
            {
              label: 'Total Escrowed',
              value: pageStats ? formatMoney(pageStats.totalEscrowed) : '…',
              icon: 'dollarSign',
              color: 'green',
            },
            {
              label: listMode === 'browse' ? 'In Diligence' : 'My Portfolio',
              value: String(listMode === 'browse' ? (pageStats?.inDiligence ?? 0) : (pageStats?.myPortfolio ?? 0)),
              icon: 'trendUp',
              color: 'gold',
            },
            {
              label: 'Pending Actions',
              value: String(pageStats?.pendingActions ?? 0),
              icon: 'alertTriangle',
              color: 'red',
            },
          ].map((stat, idx) => (
            <div key={idx} className="card p-5">
              <div className="flex justify-between items-start mb-3">
                <span className="text-xs font-medium text-gray-500">{stat.label}</span>
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                  stat.color === 'blue' ? 'bg-blue-50 text-blue-600' :
                  stat.color === 'gold' ? 'bg-yellow-50 text-yellow-600' :
                  stat.color === 'green' ? 'bg-green-50 text-green-600' :
                  'bg-red-50 text-red-600'
                }`}>
                  <RsIcon name={stat.icon} size={20} className="w-5 h-5" />
                </div>
              </div>
              <div className="text-2xl font-extrabold text-navy">{stat.value}</div>
            </div>
          ))}
        </div>
        )}

        {/* Filters */}
        <div className={isPortal ? 'portal-filter-tabs' : 'flex items-center justify-between mb-6'}>
          <div className="flex gap-2 flex-wrap">
            {canBrowse && !isPortal && (
              <>
                <button
                  type="button"
                  onClick={() => setListMode('my')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${listMode === 'my' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  {role === 'GP' ? 'My Deals' : 'My Portfolio'}
                </button>
                <button
                  type="button"
                  onClick={() => setListMode('browse')}
                  className={`px-4 py-2 rounded-lg text-sm font-medium ${listMode === 'browse' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  Browse Marketplace
                </button>
                <span className="w-px h-8 bg-gray-200 mx-1 self-center" />
              </>
            )}
            {isPortal && listMode === 'my' && PORTAL_STAGE_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPortalStageFilter(tab.id)}
                className={`portal-filter-tab${portalStageFilter === tab.id ? ' active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
            {isPortal && listMode === 'browse' && PORTAL_BROWSE_FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setPortalBrowseFilter(tab.id)}
                className={`portal-filter-tab${portalBrowseFilter === tab.id ? ' active' : ''}`}
              >
                {tab.label}
              </button>
            ))}
            {!isPortal && ['all', 'active', 'closed'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium capitalize ${activeTab === tab ? 'bg-primary-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                {tab === 'all' ? 'All Deals' : tab}
              </button>
            ))}
          </div>
          {!isPortal && (
          <div className="relative">
            <RsIcon name="search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && loadDeals(searchQuery)}
              className="pl-9 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
              placeholder="Search deals..."
            />
          </div>
          )}
        </div>

        {portalTable && !loadingDeals && (
          <PortalDealsTable deals={filteredDeals} onView={handleSelectDeal} />
        )}
        {portalBrowse && !loadingDeals && (
          <PortalBrowseDeals
            deals={filteredDeals}
            onView={handleSelectDeal}
            onJoin={handleJoinDeal}
            role={role}
          />
        )}

        {isPortal && loadingDeals && (
          <p className="text-sm text-gray-500 text-center py-12">Loading deals…</p>
        )}

        {isPortal && selectedDeal && !gpInProgress && listMode !== 'browse' && (
          <PortalDealDetail
            deal={selectedDeal}
            role={role}
            listMode={listMode}
            onClose={closeDealPreview}
            onChat={handleOpenChat}
            onJoin={handleJoinDeal}
            onCompleteAction={handleCompleteAction}
          />
        )}

        {isPortal && selectedDeal && listMode === 'browse' && (
          <div
            className="portal-deal-preview-overlay show"
            onClick={closeDealPreview}
            role="presentation"
          >
            <div
              className="portal-deal-preview-dialog"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
              aria-label={`Deal preview: ${selectedDeal.title}`}
            >
              <PortalDealDetail
                deal={selectedDeal}
                role={role}
                listMode={listMode}
                onClose={closeDealPreview}
                onChat={handleOpenChat}
                onJoin={handleJoinDeal}
                onCompleteAction={handleCompleteAction}
              />
            </div>
          </div>
        )}

        {/* Deals List (detail cards) */}
        {(!portalTable && !portalBrowse) && (
        <div className="space-y-6">
          {loadingDeals && <p className="text-sm text-gray-500">Loading deals…</p>}
          {!loadingDeals && filteredDeals.length === 0 && (
            <p className="text-sm text-gray-500">
              {listMode === 'browse' ? 'No active deals in marketplace yet.' : 'No deals found.'}
            </p>
          )}
          {filteredDeals.map(deal => (
            <div key={deal.id} className="card overflow-hidden">
              {/* Deal Header */}
              <div className="p-5 border-b border-gray-100">
                <div className="flex justify-between items-start">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center">
                      <RsIcon name="briefcase" size={24} className="w-6 h-6 text-primary-500" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-navy">{deal.title}</h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-500 flex-wrap">
                        {listMode === 'browse' && deal.isParticipant && (
                          <span className="badge badge-green text-xs">In portfolio</span>
                        )}
                        <span>{deal.gp}</span>
                        <span>•</span>
                        <span>Target: {deal.target}</span>
                        <span>•</span>
                        <span>Raised: {deal.raised}</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`badge ${
                      deal.currentPhase === 'dd' ? 'badge-green' :
                      deal.currentPhase === 'term_sheet' ? 'badge-blue' :
                      deal.currentPhase === 'escrow' ? 'badge-yellow' :
                      'badge-blue'
                    }`}>
                      {deal.currentPhase?.replace('_', ' ')}
                    </span>
                    <p className="text-sm font-bold text-navy mt-1">{deal.progress}%</p>
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="mt-4">
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-primary-500 h-2 rounded-full transition-all" style={{ width: `${deal.progress}%` }} />
                  </div>
                </div>
              </div>

              {/* Phase Timeline */}
              <div className="px-5 py-4 bg-gray-50">
                <div className="flex items-center">
                  {DEAL_PHASES.map((phase, idx) => {
                    const dealPhase = deal.phases.find(p => p.key === phase.key)
                    const status = dealPhase ? PHASE_STATUS[dealPhase.status] : PHASE_STATUS.pending
                    const isLast = idx === DEAL_PHASES.length - 1
                    return (
                      <React.Fragment key={phase.key}>
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${status.color}`}>
                            <RsIcon name={phase.icon} size={16} className={`w-4 h-4 ${dealPhase?.status === 'completed' ? 'text-white' : 'text-gray-500'}`} />
                          </div>
                          <span className="text-xs text-gray-500 mt-1 whitespace-nowrap">{phase.label}</span>
                        </div>
                        {!isLast && (
                          <div className={`flex-1 h-0.5 mx-2 ${status.line}`} />
                        )}
                      </React.Fragment>
                    )
                  })}
                </div>
              </div>

              {(role === 'GP' || role === 'Admin') && deal.currentPhase === 'matching' && (
                <MatchedLpsPanel dealId={deal.id} />
              )}

              {deal.currentPhase === 'escrow' && (
                <EscrowPanel dealId={deal.id} role={role} isParticipant={deal.isParticipant} />
              )}

              {/* Pending Actions */}
              {deal.pendingActions.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-navy mb-3">⚠️ Action Required</h4>
                  <div className="space-y-2">
                    {deal.pendingActions.map(action => (
                      <div key={action.id} className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <RsIcon name="alertTriangle" size={20} className="w-5 h-5 text-yellow-600" />
                          <div>
                            <p className="text-sm font-medium text-navy">{action.title}</p>
                            <p className="text-xs text-gray-500">Due: {action.due}</p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {action.type === 'nda' && (
                            <button
                              onClick={() => handleSignNDA(deal.id)}
                              className="px-3 py-1.5 bg-primary-500 text-white rounded-lg text-xs font-medium hover:bg-primary-600"
                            >
                              Sign NDA
                            </button>
                          )}
                          <button
                            onClick={() => handleCompleteAction(deal.id, action.id)}
                            className="px-3 py-1.5 bg-green-500 text-white rounded-lg text-xs font-medium hover:bg-green-600"
                          >
                            Complete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timeline */}
              {deal.timeline.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-100">
                  <h4 className="text-sm font-semibold text-navy mb-3">📅 Timeline</h4>
                  <div className="space-y-3">
                    {deal.timeline.slice(0, 5).map((event, idx) => (
                      <div key={idx} className="flex gap-3">
                        <div className="w-2 h-2 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                        <div>
                          <p className="text-sm text-gray-600">{event.event}</p>
                          <p className="text-xs text-gray-400">{event.date}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Deal Actions */}
              <div className="px-5 py-4 border-t border-gray-100 flex gap-2 flex-wrap">
                <button
                  onClick={() => handleSelectDeal(deal)}
                  className="px-4 py-2 bg-primary-50 text-primary-600 rounded-lg text-sm font-medium hover:bg-primary-100"
                >
                  View Details
                </button>
                {['LP', 'Startup'].includes(role) && listMode === 'browse' && !deal.isParticipant && deal.currentPhase !== 'closed' && (
                  <button
                    type="button"
                    onClick={() => handleJoinDeal(deal.id)}
                    className="px-4 py-2 bg-gold-500 text-white rounded-lg text-sm font-medium hover:bg-gold-600"
                  >
                    Join Portfolio
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleOpenChat(deal)}
                  className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 flex items-center gap-1"
                >
                  <RsIcon name="messageSquare" size={16} className="w-4 h-4" /> Chat
                </button>
                <button className="px-4 py-2 bg-gray-50 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-100 flex items-center gap-1">
                  <RsIcon name="video" size={16} className="w-4 h-4" /> Meeting
                </button>
                {role === 'Admin' && deal.currentPhase !== 'closed' && (
                  <button
                    onClick={() => handleAdvancePhase(deal.id, deal.currentPhase, DEAL_PHASES[DEAL_PHASES.findIndex(p => p.key === deal.currentPhase) + 1]?.key)}
                    className="px-4 py-2 bg-green-50 text-green-600 rounded-lg text-sm font-medium hover:bg-green-100 ml-auto"
                  >
                    Advance Phase <RsIcon name="arrowRight" size={16} className="w-4 h-4 inline" />
                  </button>
                )}
              </div>
            </div>
          ))}
          {filteredDeals.length === 0 && (
            <div className="text-center py-16">
              <RsIcon name="briefcase" size={48} className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="text-gray-500">No deals found</p>
              {role === 'GP' && (
                <button
                  type="button"
                  onClick={() => (isPortal ? setSearchParams({ action: 'create' }) : setShowCreate(true))}
                  className="text-primary-500 text-sm font-medium mt-2 hover:underline"
                >
                  Post your first deal
                </button>
              )}
            </div>
          )}
        </div>
        )}

        </>
        )}

      {/* Create Deal Modal */}
      {showCreate && !isPortal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl w-full max-w-lg p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-navy">Post New Deal</h2>
              <button onClick={() => setShowCreate(false)} className="p-1 hover:bg-gray-100 rounded-lg">
                <RsIcon name="close" size={20} className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <form onSubmit={handleCreateDeal} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Deal Title</label>
                <input
                  type="text"
                  value={newDeal.title}
                  onChange={(e) => setNewDeal(prev => ({ ...prev, title: e.target.value }))}
                  className="input-field"
                  placeholder="e.g., China Growth VI"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newDeal.description}
                  onChange={(e) => setNewDeal(prev => ({ ...prev, description: e.target.value }))}
                  className="input-field min-h-[100px]"
                  placeholder="Describe the investment opportunity..."
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target Amount</label>
                  <input
                    type="text"
                    value={newDeal.targetAmount}
                    onChange={(e) => setNewDeal(prev => ({ ...prev, targetAmount: e.target.value }))}
                    className="input-field"
                    placeholder="e.g., $500M"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Industry</label>
                  <input
                    type="text"
                    value={newDeal.industry}
                    onChange={(e) => setNewDeal(prev => ({ ...prev, industry: e.target.value }))}
                    className="input-field"
                    placeholder="e.g., PE Fund"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                <input
                  type="text"
                  value={newDeal.location}
                  onChange={(e) => setNewDeal(prev => ({ ...prev, location: e.target.value }))}
                  className="input-field"
                  placeholder="e.g., China"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600"
                >
                  Post Deal
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="admin-modal-overlay show" onClick={() => !addingTask && setShowTaskModal(false)} role="presentation">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="admin-modal-header">
              <h3>Add Task</h3>
              <ModalClose onClick={() => setShowTaskModal(false)} disabled={addingTask} />
            </div>
            <form onSubmit={submitNewTask} className="admin-modal-body">
              <label className="block text-sm font-medium text-gray-700 mb-1">Task title</label>
              <input
                type="text"
                className="input-field"
                value={taskTitle}
                onChange={(e) => setTaskTitle(e.target.value)}
                placeholder="e.g., Upload updated financial model"
                required
                autoFocus
              />
              <div className="flex gap-3 pt-4">
                <button type="button" className="admin-btn secondary flex-1" onClick={() => setShowTaskModal(false)} disabled={addingTask}>
                  Cancel
                </button>
                <button type="submit" className="admin-btn primary flex-1" disabled={addingTask}>
                  {addingTask ? 'Adding…' : 'Add Task'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageShell>
  )
}
